import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Organisation from 'shared/db/services/Organisation';
import * as yup from 'yup';

import { BackLinkButton, PhoneNumber } from '@/components';
import { EMAIL_REGEXP } from '@/constants/regex';
import { useImagesContext } from '@/context/ImagesContext';
import { useUserContext } from '@/context/UserContext';
import { useDatabase } from '@/hooks';
import { useOrganisationData } from '@/hooks/useOrganisationData';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import Logger from '@/services/logger';
import { Snackbar } from '@/services/toastNotifications';

type FormValues = {
    address: string;
    payment_instructions: string;
    footer: string;
    use_alternative_email: boolean;
    alternative_email: string;
    use_alternative_phone: boolean;
    alternative_phone_number: string;
    alternative_phone_prefix: string;
};

const helperText = 'InvoiceSystem:business_details:tip';

const validationSchema = yup.object().shape({
    address: yup
        .string()
        .test(
            'format',
            'InvoiceSystem:business_details:errors:incorrect_format',
            (value) => {
                if (!value) return true;
                const lines = value.split('\n');
                if (lines.length > 4) return false;
                return lines.every((line) => line.length <= 35);
            },
        )
        .required('InvoiceSystem:business_details:errors:required_field'),
    payment_instructions: yup
        .string()
        .required('InvoiceSystem:business_details:errors:required_field'),
    alternative_email: yup.string().matches(EMAIL_REGEXP, {
        message: 'SignIn:error:email_must_be_valid_email_adresss',
    }),
});

function BusinessDetailsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getDatabase } = useDatabase();
    const { ImagesService } = useImagesContext();
    const { userProfileData } = useUserContext();
    const { organisation, isLoading } = useOrganisationData();

    const organisationService = useMemo(
        () =>
            new Organisation({
                database: getDatabase(),
                imageService: ImagesService,
                logDBAction: Logger.logRecordActivity,
                validation: databaseValidation,
            }),
        [getDatabase, ImagesService],
    );

    const handleFormSubmit = useCallback(
        async (values: FormValues) => {
            try {
                await organisationService.update({
                    payload: {
                        alternativeAddress: values.address,
                        email: organisation?.email || '',
                        paymentInstructons: values.payment_instructions,
                        alternativeEmail: values.use_alternative_email
                            ? values.alternative_email
                            : '',
                        isAlternativeEmailEnabled: values.use_alternative_email,
                        alternativePhone: values.use_alternative_phone
                            ? values.alternative_phone_number
                            : '',
                        alternativePhonePrefix: values.use_alternative_phone
                            ? values.alternative_phone_prefix
                            : '',
                        isAlternativePhoneEnabled: values.use_alternative_phone,
                        footerMessage: values.footer,
                    },
                    userId: userProfileData?.id || '',
                });

                navigate(ROUTE.settings);
            } catch {
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: { variant: 'error' },
                });
            }
        },
        [
            organisationService,
            organisation?.email,
            userProfileData?.id,
            navigate,
            t,
        ],
    );

    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        validationSchema,
        initialValues: {
            address: organisation?.alternativeAddress || '',
            payment_instructions: organisation?.paymentInstructions || '',
            footer: organisation?.footerMessage || '',
            use_alternative_email:
                organisation?.isAlternativeEmailEnabled || false,
            alternative_email: organisation?.alternativeEmail || '',
            use_alternative_phone:
                organisation?.isAlternativePhoneEnabled || false,
            alternative_phone_number:
                organisation?.alternativePhoneNumber || '',
            alternative_phone_prefix:
                organisation?.alternativePhonePrefix || '+1',
        },
        onSubmit: handleFormSubmit,
    });

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 4 }}>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 4,
                }}
            >
                <BackLinkButton
                    to={ROUTE.settings}
                    text="Settings"
                    testID="BusinessDetailsPage-BackButton"
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RequestQuoteOutlined fontSize="small" />
                <Typography fontSize={16} fontWeight="bold">
                    {t('InvoiceSystem:business_details:title')}
                </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ maxWidth: 500 }}>
                <Alert severity="info" sx={{ mb: 4 }}>
                    {t('InvoiceSystem:business_details:disclaimer_prefix')}
                    {t('InvoiceSystem:business_details:disclaimer')}
                </Alert>

                <form onSubmit={formik.handleSubmit}>
                    {/* Address Section */}
                    <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
                        {t('InvoiceSystem:business_details:address_label')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        name="address"
                        label={t(
                            'InvoiceSystem:business_details:address_label',
                        )}
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.address &&
                            Boolean(formik.errors.address)
                        }
                        helperText={
                            formik.touched.address && formik.errors.address
                                ? t(formik.errors.address)
                                : t(helperText)
                        }
                        sx={{ mb: 4 }}
                        data-test-id="BusinessDetailsPage-AddressField"
                    />

                    {/* Payment Instructions Section */}
                    <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
                        {t('InvoiceSystem:business_details:payment_label')}
                    </Typography>
                    <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {t(
                            'InvoiceSystem:business_details:payment_description',
                        )}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        name="payment_instructions"
                        label={t(
                            'InvoiceSystem:business_details:payment_label',
                        )}
                        value={formik.values.payment_instructions}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.payment_instructions &&
                            Boolean(formik.errors.payment_instructions)
                        }
                        helperText={
                            formik.touched.payment_instructions &&
                            formik.errors.payment_instructions
                                ? t(formik.errors.payment_instructions)
                                : t(helperText)
                        }
                        sx={{ mb: 4 }}
                        data-test-id="BusinessDetailsPage-PaymentInstructionsField"
                    />

                    {/* Footer Text Section */}
                    <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
                        {t('InvoiceSystem:business_details:footer_title')}
                    </Typography>
                    <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        <Typography
                            component="span"
                            fontSize={14}
                            fontWeight="bold"
                            color="text.secondary"
                        >
                            {t(
                                'InvoiceSystem:business_details:footer_disclaimer_prefix',
                            )}
                        </Typography>
                        {t('InvoiceSystem:business_details:footer_disclaimer')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        name="footer"
                        label={t('InvoiceSystem:business_details:footer_title')}
                        value={formik.values.footer}
                        onChange={formik.handleChange}
                        helperText={t(helperText)}
                        sx={{ mb: 4 }}
                        data-test-id="BusinessDetailsPage-FooterField"
                    />

                    {/* Contact Information Section */}
                    <Typography fontWeight="bold" fontSize={16} sx={{ mb: 1 }}>
                        {t(
                            'InvoiceSystem:business_details:contact_information:title',
                        )}
                    </Typography>
                    <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        {t(
                            'InvoiceSystem:business_details:contact_information:disclaimer',
                        )}
                        <Typography
                            component="span"
                            fontSize={14}
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                        >
                            {t(
                                'InvoiceSystem:business_details:contact_information:disclaimer_suffix',
                            )}
                        </Typography>
                        .
                    </Typography>

                    {/* Alternative Email */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formik.values.use_alternative_email}
                                onChange={formik.handleChange}
                                name="use_alternative_email"
                                data-test-id="BusinessDetailsPage-AlternativeEmailSwitch"
                            />
                        }
                        label={t(
                            'InvoiceSystem:business_details:contact_information:alternative_email',
                        )}
                        labelPlacement="start"
                        sx={{
                            mb: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            ml: 0,
                        }}
                    />
                    {formik.values.use_alternative_email ? (
                        <TextField
                            fullWidth
                            name="alternative_email"
                            label={t(
                                'InvoiceSystem:business_details:email_input_placeholder',
                            )}
                            type="email"
                            value={formik.values.alternative_email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.alternative_email &&
                                Boolean(formik.errors.alternative_email)
                            }
                            helperText={
                                formik.touched.alternative_email &&
                                formik.errors.alternative_email
                                    ? t(formik.errors.alternative_email)
                                    : undefined
                            }
                            sx={{ mb: 3 }}
                            data-test-id="BusinessDetailsPage-AlternativeEmailField"
                        />
                    ) : (
                        <Typography
                            fontSize={14}
                            color="text.secondary"
                            sx={{ mb: 3 }}
                        >
                            {t(
                                'InvoiceSystem:business_details:contact_information:currently_using',
                            )}{' '}
                            {organisation?.email || '-'}
                        </Typography>
                    )}

                    {/* Alternative Phone */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formik.values.use_alternative_phone}
                                onChange={formik.handleChange}
                                name="use_alternative_phone"
                                data-test-id="BusinessDetailsPage-AlternativePhoneSwitch"
                            />
                        }
                        label={t(
                            'InvoiceSystem:business_details:contact_information:alternative_phone',
                        )}
                        labelPlacement="start"
                        sx={{
                            mb: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            ml: 0,
                        }}
                    />
                    {formik.values.use_alternative_phone ? (
                        <PhoneNumber
                            fullWidth
                            name="alternative_phone_number"
                            label={t(
                                'InvoiceSystem:business_details:contact_information:alternative_phone',
                            )}
                            value={
                                formik.values.alternative_phone_prefix +
                                formik.values.alternative_phone_number
                            }
                            onChange={(phone, data) => {
                                const dialCode = (data as { dialCode: string })
                                    .dialCode;
                                const phoneNumber = (phone as string).substring(
                                    dialCode.length + 1,
                                );
                                formik.setFieldValue(
                                    'alternative_phone_number',
                                    phoneNumber,
                                );
                                formik.setFieldValue(
                                    'alternative_phone_prefix',
                                    `+${dialCode}`,
                                );
                            }}
                            defaultCountry="us"
                            sx={{ mb: 3 }}
                            data-test-id="BusinessDetailsPage-AlternativePhoneField"
                        />
                    ) : (
                        <Typography
                            fontSize={14}
                            color="text.secondary"
                            sx={{ mb: 3 }}
                        >
                            {t(
                                'InvoiceSystem:business_details:contact_information:currently_using',
                            )}{' '}
                            {organisation?.phoneNumber
                                ? `${organisation.phonePrefix || ''}${
                                      organisation.phoneNumber
                                  }`
                                : '-'}
                        </Typography>
                    )}

                    {/* Save Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={formik.isSubmitting || !formik.dirty}
                        data-test-id="BusinessDetailsPage-SaveButton"
                    >
                        {formik.isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            t('Actions:save_changes')
                        )}
                    </Button>
                </form>
            </Box>
        </Box>
    );
}

export default BusinessDetailsPage;
